import { Link } from "react-router-dom";
import { todaySchedule } from "@/mocks/admin/schedule";

const statusStyles: Record<
  string,
  { bg: string; text: string; dot: string; label: string; ring: string }
> = {
  "in-progress": {
    bg: "bg-ink-900",
    text: "text-white",
    dot: "bg-emerald-400",
    label: "In Progress",
    ring: "ring-emerald-300/50",
  },
  scheduled: {
    bg: "bg-cream-100",
    text: "text-ink-700",
    dot: "bg-ink-400",
    label: "Scheduled",
    ring: "ring-ink-200",
  },
  completed: {
    bg: "bg-cream-100",
    text: "text-ink-500",
    dot: "bg-ink-300",
    label: "Completed",
    ring: "ring-ink-200",
  },
};

function shortAddr(addr: string) {
  return addr.split(",")[0];
}
function cityOf(addr: string) {
  const parts = addr.split(",").map((p) => p.trim());
  return parts.length >= 2 ? parts[1] : "";
}

export default function TodaySchedule() {
  const inProgress = todaySchedule.filter((m) => m.status === "in-progress").length;
  const scheduled = todaySchedule.filter((m) => m.status === "scheduled").length;

  return (
    <div className="bg-white border border-ink-100 rounded-lg">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 pt-5 pb-4 border-b border-ink-100">
        <div>
          <h3 className="font-display text-lg font-bold text-ink-900">Today&apos;s Schedule</h3>
          <p className="text-xs text-ink-500 mt-0.5">
            {todaySchedule.length} moves on the board · Mon, June 1
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="hidden sm:flex items-center gap-2 mr-1 text-[11px] text-ink-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              <span className="font-semibold text-ink-900">{inProgress}</span> active
            </span>
            <span className="text-ink-300">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-ink-400"></span>
              <span className="font-semibold text-ink-900">{scheduled}</span> scheduled
            </span>
          </div>
          <Link
            to="/projects"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-line"></i>
            Calendar
          </Link>
        </div>
      </div>

      {/* Rows */}
      <ul className="divide-y divide-ink-100">
        {todaySchedule.map((m) => {
          const s = statusStyles[m.status] || statusStyles.scheduled;
          return (
            <li key={m.id}>
              <Link
                to={`/projects/${m.id}`}
                className="block px-6 py-4 hover:bg-cream-50 transition-colors cursor-pointer group"
              >
              <div className="flex items-center gap-6">
                {/* Time block */}
                <div className="w-16 shrink-0 text-center border-r border-ink-100 pr-5">
                  <div className="font-display text-xl font-bold text-ink-900 leading-none tabular-nums">
                    {m.startTime}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold mt-1.5">
                    {m.duration}
                  </div>
                </div>

                {/* Customer + project */}
                <div className="w-56 shrink-0 min-w-0 hidden lg:block">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-ink-900 text-sm truncate">{m.customer}</h4>
                  </div>
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="font-mono text-ink-400">{m.id}</span>
                    <span className="text-ink-300">·</span>
                    <span className="text-ink-500 truncate">{m.moveType}</span>
                  </div>
                </div>

                {/* Compact customer for smaller screens */}
                <div className="flex-1 min-w-0 lg:hidden">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-ink-900 text-sm truncate">{m.customer}</h4>
                    <span className="text-[10px] font-mono text-ink-400 shrink-0">{m.id}</span>
                  </div>
                  <p className="text-xs text-ink-500 truncate">{m.moveType}</p>
                </div>

                {/* Route (visible on lg+) */}
                <div className="flex-1 min-w-0 hidden lg:flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold mb-0.5">
                      From
                    </div>
                    <div className="text-xs text-ink-700 truncate">{shortAddr(m.from)}</div>
                    <div className="text-[11px] text-ink-500 truncate">{cityOf(m.from)}</div>
                  </div>
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-cream-100 text-ink-400 shrink-0">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold mb-0.5">
                      To
                    </div>
                    <div className="text-xs text-ink-900 font-medium truncate">
                      {shortAddr(m.to)}
                    </div>
                    <div className="text-[11px] text-ink-500 truncate">{cityOf(m.to)}</div>
                  </div>
                </div>

                {/* Truck */}
                <div className="hidden xl:flex items-center gap-2 w-40 shrink-0 text-ink-600">
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-cream-100 text-ink-700 shrink-0">
                    <i className="ri-truck-line text-sm"></i>
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
                      Truck
                    </div>
                    <div className="text-xs font-semibold text-ink-900 truncate">{m.truck}</div>
                  </div>
                </div>

                {/* Crew avatars */}
                <div className="hidden md:flex flex-col items-end gap-2 shrink-0 w-32">
                  <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
                    Crew · {m.crew.length}
                  </div>
                  <div className="flex -space-x-1.5">
                    {m.crew.slice(0, 4).map((c) => (
                      <div
                        key={c.name}
                        title={c.name}
                        className={`w-7 h-7 rounded-full ${c.color} text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white`}
                      >
                        {c.avatar}
                      </div>
                    ))}
                    {m.crew.length > 4 && (
                      <div className="w-7 h-7 rounded-full bg-ink-200 text-ink-700 text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                        +{m.crew.length - 4}
                      </div>
                    )}
                  </div>
                </div>

                {/* Status pill */}
                <div className="shrink-0 w-28 flex justify-end">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold ${s.bg} ${s.text} ring-1 ${s.ring}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}></span>
                    {s.label}
                  </span>
                </div>

                {/* Chevron */}
                <span className="hidden md:flex w-5 h-5 items-center justify-center text-ink-300 group-hover:text-ink-700 group-hover:translate-x-0.5 transition-all shrink-0">
                  <i className="ri-arrow-right-s-line"></i>
                </span>
              </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}